import moment from "moment";
export { API_DATE } from "../Shared/formatters";
export { APPROVAL_STATUSES } from "../ShiftEarnings/shiftEarningsData";

// penalty_type values the attendance calculations write and the payrun reads.
export const PENALTY_TYPES = ["Late Entry", "Early Exit", "Break Penalty", "Absent"];

export const BASIS_OPTIONS = [
  { value: "shift", label: "Shift" },
  { value: "hours", label: "Hours" },
  { value: "amount", label: "Amount" },
];

// "HH:MM" (or "HH:MM:SS" from the server; seconds ignored) or decimal hours
// -> hours as a number (null when invalid or 24h+).
export const hoursValue = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  if (text.includes(":")) {
    const match = /^(\d{1,2}):([0-5]\d)(:[0-5]\d)?$/.exec(text);
    if (!match) return null;
    const hours = Number(match[1]) + Number(match[2]) / 60;
    return hours < 24 ? hours : null;
  }
  const hours = Number(text);
  return Number.isFinite(hours) && hours >= 0 && hours < 24 ? hours : null;
};

// Quantity as a number for the preview (shifts or hours).
export const quantityValue = (basis, quantity) =>
  basis === "hours" ? hoursValue(quantity) : Number(quantity);

export const emptyForm = () => ({ employee_code: "", date: moment().format("YYYY-MM-DD"), penalty_type: "Late Entry", shift_name: "", basis: "shift", quantity: "", payment_factor: "1", rate: "", amount: "", pay_schedule_id: "", status: "Pending" });

export const rowToForm = (row) => ({
  employee_code: row.employee_code,
  employee_name: row.employee_name,
  image_url: row.image_url || "",
  department_name: row.department_name || row.department || "",
  designation: row.designation_name || row.designation || "",
  date: row.date,
  penalty_type: row.penalty_type || "",
  shift_name: row.shift_name || "",
  basis: row.basis || "shift",
  quantity: row.basis === "amount" ? "" : String(row.quantity ?? ""),
  payment_factor: String(row.payment_factor ?? "1"),
  rate: String(row.rate ?? ""),
  amount: row.basis === "amount" ? String(row.payable_amount ?? "") : "",
  pay_schedule_id: row.pay_schedule_id == null ? "" : String(row.pay_schedule_id),
  status: row.status,
  payable_amount: row.payable_amount,
});

// Same formula as the server: amount = quantity x rate x factor (amount basis:
// the amount). A preview never replaces server amounts or enters the payload.
export const previewPenalty = (form, initial) => {
  if (initial && ["basis", "quantity", "payment_factor", "rate", "amount"].every((key) => form[key] === initial[key])) {
    return { payable_amount: initial.payable_amount };
  }
  if (form.basis === "amount") return { payable_amount: Number(form.amount) || 0 };
  const quantity = quantityValue(form.basis, form.quantity) || 0;
  return { payable_amount: quantity * (Number(form.rate) || 0) * (Number(form.payment_factor) || 0) };
};

const isNumberAtLeastZero = (value) => String(value ?? "").trim() !== "" && Number.isFinite(Number(value)) && Number(value) >= 0;

// `types`: the penalty types from the server (usePenaltyTypes), or the
// built-in PENALTY_TYPES.
export const validateForm = (form, initial, types = PENALTY_TYPES) => {
  const errors = {};
  if (!form.employee_code.trim()) errors.employee_code = "Select an employee.";
  if (!moment(form.date, "YYYY-MM-DD", true).isValid()) errors.date = "Enter a valid date.";
  if (!types.includes(form.penalty_type)) errors.penalty_type = "Select a penalty type.";
  if (!BASIS_OPTIONS.some((option) => option.value === form.basis)) errors.basis = "Select a basis.";
  if (form.basis === "amount") {
    if (!isNumberAtLeastZero(form.amount)) errors.amount = "Enter a number of 0 or more.";
  } else {
    if (form.basis === "hours" ? hoursValue(form.quantity) === null : !isNumberAtLeastZero(form.quantity)) {
      errors.quantity = form.basis === "hours" ? "Enter hours as HH:MM or a number below 24." : "Enter a number of 0 or more.";
    }
    for (const key of ["rate", "payment_factor"]) {
      if (!isNumberAtLeastZero(form[key])) errors[key] = "Enter a number of 0 or more.";
    }
  }
  if (initial) Object.keys(errors).forEach((key) => { if (form[key] === initial[key] && form.basis === initial.basis) delete errors[key]; });
  return errors;
};
